import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Send, Loader2, Sparkles, MessageCircle, Plus, AlertTriangle } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { API } from '../utils/apiBase';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { useFeatureGate } from '../utils/useFeatureGate';


const SUGGESTED_PROMPTS = [
  "What should I focus on this week to land interviews?",
  "Rewrite my LinkedIn headline for a senior PM role.",
  "How should I follow up on an application from 8 days ago?",
  "Give me a 30-second elevator pitch based on my resume.",
  "What's one metric I should add to my top 3 bullets?",
];

const Message = ({ role, text }) => {
  const isUser = role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
    >
      <div
        className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? 'bg-[#002fa7] text-white' : 'bg-white border border-[#e5e7eb] text-[#0f1115]'}`}
        data-testid={`msg-${role}`}
      >
        {text}
      </div>
    </motion.div>
  );
};

export default function CareerCoach() {
  const { user, loading: authLoading } = useAuth();
  const { checkGate, gateModal } = useFeatureGate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useDocumentMeta({
    title: 'Sage — Your Honest AI Career Copilot | Ascend',
    description: 'Sage is your always-on AI career copilot. Already knows your resume. Honest, transparent answers on what to fix, how to follow up, and how to land the role faster.',
    path: '/coach',
  });

  // `isEmptyState` = user is already in a blank "new conversation" state, so
  // showing a "+ New conversation" button is confusing (clicking does nothing
  // visible). We hide the button in this state.
  const isEmptyState = sessionId === null && messages.length === 0;

  useEffect(() => {
    if (authLoading || !user) return;
    axios.get(`${API}/career-coach/sessions`, { withCredentials: true })
      .then(res => setSessions(res.data.sessions || []))
      .catch(() => {});
  }, [authLoading, user]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/career-coach/history/${sid}`, { withCredentials: true });
      setSessionId(sid);
      setMessages(res.data.messages || []);
    } catch (e) {
      setErr('Failed to load session');
    }
  };

  const newSession = () => {
    const hadContent = sessionId !== null || messages.length > 0;
    setSessionId(null);
    setMessages([]);
    setErr('');
    setDraft('');
    if (hadContent) {
      toast.success('Started a new conversation');
    }
    // Defer focus to next tick so the input renders in the "ready" state first.
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Auto-focus the chat input on first render so users can start typing
  // immediately without needing to click anywhere. Only fires for the
  // initial-empty state; subsequent session loads don't steal focus.
  useEffect(() => {
    if (isEmptyState && !authLoading && user) {
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const send = async (text) => {
    const msg = (text ?? draft).trim();
    if (!msg || busy) return;
    if (await checkGate('sage_chat')) return;
    setErr('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setDraft('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/career-coach/chat`, {
        message: msg, session_id: sessionId,
      }, { withCredentials: true });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.reply }]);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Sage is unavailable. Try again.');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="career-coach-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* Left: sessions sidebar */}
        <div className="lg:col-span-1 space-y-4">
          {!isEmptyState && (
            <button
              onClick={newSession}
              className="w-full px-4 py-3 bg-[#002fa7] text-white font-semibold hover:bg-[#001d6a] transition-colors inline-flex items-center justify-center gap-2"
              data-testid="new-coach-session"
            >
              <Plus size={16} /> New conversation
            </button>
          )}
          <div className="bg-white border border-[#e5e7eb]" data-testid="coach-sessions">
            <div className="px-4 py-3 border-b border-[#e5e7eb] text-xs uppercase tracking-wider font-bold text-[#6b7280]">
              Recent
            </div>
            {sessions.length === 0 ? (
              <p className="p-4 text-xs text-[#9ca3af]">No prior conversations yet.</p>
            ) : (
              <ul className="divide-y divide-[#e5e7eb]">
                {sessions.map(s => (
                  <li key={s.session_id}>
                    <button
                      onClick={() => loadSession(s.session_id)}
                      className={`w-full text-left px-4 py-3 text-xs hover:bg-[#f4f5f7] transition-colors ${sessionId === s.session_id ? 'bg-[#002fa7]/5' : ''}`}
                      data-testid={`session-${s.session_id}`}
                    >
                      <div className="text-[#0f1115] line-clamp-2">{s.preview || '(empty)'}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: chat */}
        <div className="lg:col-span-3 flex flex-col bg-white border border-[#e5e7eb] min-h-[calc(100vh-11rem)]">
          <div className="px-6 py-4 border-b border-[#e5e7eb] flex items-center gap-3">
            <div className="w-10 h-10 bg-[#002fa7]/10 flex items-center justify-center">
              <Sparkles size={18} className="text-[#002fa7]" />
            </div>
            <div>
              <h1 className="font-satoshi text-lg font-black text-[#0f1115]" data-testid="sage-heading">Sage</h1>
              <p className="text-xs text-[#6b7280]">Your AI career copilot · Honest. Transparent. Always-on.</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 bg-[#f9fafb]" data-testid="coach-messages">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto">
                <MessageCircle size={40} className="text-[#002fa7]/30 mb-4" />
                <h2 className="font-satoshi text-xl font-black text-[#0f1115] mb-2">Hey, I'm Sage.</h2>
                <p className="text-sm text-[#6b7280] mb-6">I already have your latest resume context loaded. Ask me anything — what to fix this week, how to follow up, how to negotiate. I won't sugarcoat it.</p>
                <div className="w-full space-y-2">
                  {SUGGESTED_PROMPTS.map(p => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      className="w-full text-left px-4 py-2 text-sm border border-[#e5e7eb] hover:border-[#002fa7] hover:bg-[#002fa7]/5 transition-colors text-[#4b5563]"
                      data-testid={`prompt-${p.length}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => <Message key={i} role={m.role} text={m.text} />)
            )}
            {busy && (
              <div className="flex justify-start" data-testid="coach-typing">
                <div className="bg-white border border-[#e5e7eb] px-4 py-3 text-sm text-[#6b7280] inline-flex flex-col gap-1">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Sage is thinking…
                  </span>
                  <span className="text-[11px] text-[#9ca3af]">Complex prompts can take up to 30 seconds. Hang tight.</span>
                </div>
              </div>
            )}
            {err && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-800 text-sm flex items-center gap-2" data-testid="coach-error">
                <AlertTriangle size={14} /> {err}
              </div>
            )}
          </div>

          <div className="border-t border-[#e5e7eb] p-4 flex gap-2">
            <input
              ref={inputRef}
              type="text" value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ask Sage anything…"
              className="flex-1 px-4 py-3 border border-[#e5e7eb] text-sm focus:outline-none focus:border-[#002fa7]"
              data-testid="coach-input"
              disabled={busy}
            />
            <button
              onClick={() => send()}
              disabled={busy || !draft.trim()}
              className="px-5 py-3 bg-[#002fa7] text-white font-semibold hover:bg-[#001d6a] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="coach-send"
            >
              <Send size={16} />
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
        </div>
      </div>
      {gateModal}
    </div>
  );
}
